import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiSave } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { useAdminAccess } from '../utilities/adminAccess';
import {
  coachBirthdayValue,
  coachDisplayName,
  coachRole,
  extractCoaches,
  extractEntity,
  toDateInputValue,
} from '../utilities/tournamentsApi';
import CustomModal from '../components/CustomModal';
import StadiumCrumbs from '../components/StadiumCrumbs';
import '../styles/createMatchModal.css';
import '../styles/stadiumsManagement.css';
import '../styles/tournamentsManagement.css';

const emptyForm = {
  family: '',
  name: '',
  father_name: '',
  position: '',
  birthday: '',
};

export default function ManageCoach() {
  const { teamId, coachId } = useParams();
  const isNew = !coachId;
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();
  const { canWrite } = useAdminAccess('teams');

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [teamName, setTeamName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    if (!champId && champsLoading) return;
    initPage();
  }, [teamId, coachId, champId, champsLoading]);

  const goBackToTeam = () => {
    history.push(`/teams_management/${teamId}`);
  };

  const initPage = async () => {
    setMessage({ text: '', type: '' });
    setErrors({});

    if (!champId) {
      setLoading(false);
      setMessage({ text: t('championshipsLoadError'), type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const response = await authorizedPost(dispatch, API_URLS.getCatalogTeam, {
        id: Number(teamId),
      });
      const team = extractEntity(response.data);
      setTeamName(team.name || '');

      if (isNew) {
        setForm(emptyForm);
        return;
      }

      const coach = extractCoaches(team).find((item) => Number(item.id) === Number(coachId));
      if (!coach) {
        setMessage({ text: t('tmOfficialNotFound'), type: 'error' });
        setForm(emptyForm);
        return;
      }

      setForm({
        family: coach.family || '',
        name: coach.name || '',
        father_name: coach.father_name || '',
        position: coachRole(coach),
        birthday: toDateInputValue(coachBirthdayValue(coach)),
      });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.family.trim()) nextErrors.family = t('trLastNameRequired');
    if (!form.name.trim()) nextErrors.name = t('trFirstNameRequired');
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const payload = {
        team_id: Number(teamId),
        family: form.family.trim(),
        name: form.name.trim(),
        father_name: form.father_name.trim() || null,
        description: form.position.trim() || '',
        birthdayDate: form.birthday || null,
      };

      if (isNew) {
        await authorizedPost(dispatch, API_URLS.addCoach, payload);
        setMessage({ text: t('tmOfficialCreated'), type: 'success' });
        goBackToTeam();
      } else {
        await authorizedPost(dispatch, API_URLS.changeCoach, {
          ...payload,
          id: Number(coachId),
        });
        setMessage({ text: t('stSaved'), type: 'success' });
      }
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stSaveError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await authorizedPost(dispatch, API_URLS.deleteCoach, {
        id: Number(coachId),
      });
      goBackToTeam();
    } catch (error) {
      setDeleteVisible(false);
      setMessage({ text: getApiErrorMessage(error, t('stdDeleteError')), type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const crumbs = [
    { label: t('navTeams'), to: '/teams_management' },
    { label: teamName || t('tmEditTeam'), to: `/teams_management/${teamId}` },
    { label: isNew ? t('tmNewOfficial') : (coachDisplayName(form) || t('tmEditOfficial')) },
  ];

  if (loading) {
    return (
      <div className="layoutWrapper st-layout">
        <div className="matchCenterWrapper st-page">
          <div className="matchCenterHeader">
            <div className="st-header">
              <StadiumCrumbs items={crumbs} />
            </div>
          </div>
          <div className="st-loading">
            <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
            <p>{t('trLoading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page tr-curator-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={crumbs} />
          </div>
        </div>

        <div className="st-scroll">
          <div className="st-content">
            {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

            <div className="st-block tr-curator-form">
              <h2 className="st-block-header">{isNew ? t('tmNewOfficial') : t('tmEditOfficial')}</h2>
              <div className="st-form-grid tr-curator-fields">
                <div className="st-field">
                  <label>{t('trLastName')} <span className="st-required">*</span></label>
                  <input
                    className={`st-input ${errors.family ? 'error' : ''}`}
                    value={form.family}
                    onChange={(event) => updateField('family', event.target.value)}
                  />
                  {errors.family ? <span className="st-field-error">{errors.family}</span> : null}
                </div>

                <div className="st-field">
                  <label>{t('trFirstName')} <span className="st-required">*</span></label>
                  <input
                    className={`st-input ${errors.name ? 'error' : ''}`}
                    value={form.name}
                    onChange={(event) => updateField('name', event.target.value)}
                  />
                  {errors.name ? <span className="st-field-error">{errors.name}</span> : null}
                </div>

                <div className="st-field">
                  <label>{t('trFatherName')}</label>
                  <input
                    className="st-input"
                    value={form.father_name}
                    onChange={(event) => updateField('father_name', event.target.value)}
                  />
                </div>

                <div className="st-field">
                  <label>{t('trPosition')}</label>
                  <input
                    className="st-input"
                    value={form.position}
                    placeholder={t('tmRolePh')}
                    onChange={(event) => updateField('position', event.target.value)}
                  />
                </div>

                <div className="st-field">
                  <label>{t('tmBirthDate')}</label>
                  <div className="st-native-dt">
                    <input
                      type="date"
                      className="st-input"
                      value={form.birthday}
                      onChange={(event) => updateField('birthday', event.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {canWrite ? (
        <div className="st-actions">
          {!isNew ? (
            <button
              type="button"
              className="st-btn st-btn-danger"
              disabled={saving || deleting}
              onClick={() => setDeleteVisible(true)}
            >
              {t('delete')}
            </button>
          ) : null}
          <button
            type="button"
            className="st-btn st-btn-primary"
            disabled={saving}
            onClick={handleSave}
          >
            <FiSave /> {saving ? t('trLoading') : t('stdSaveChanges')}
          </button>
        </div>
        ) : null}
      </div>

      <CustomModal
        styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={deleteVisible}
        closeModal={() => setDeleteVisible(false)}
      >
        <div className="st-delete-modal">
          <div className="customModalHeader">{t('tmDeleteOfficialTitle')}</div>
          <p>{t('tmDeleteOfficialText')}</p>
          <div className="st-delete-actions">
            <button type="button" className="btn-modal btn-secondary-modal" onClick={() => setDeleteVisible(false)}>
              {t('cancel')}
            </button>
            <button type="button" className="btn-modal btn-danger-modal" disabled={deleting} onClick={handleDelete}>
              {t('delete')}
            </button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
